import React, { useState, useEffect, useCallback, useRef } from "react";
import { Form, Button, Row, Col, Container, Card } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { showErrorToast, showSuccessToast } from "../utils/AlertUtils";
import ProfileService from "../services/ProfileService";
import { useLoading } from "../context/LoadingContext";
import DataTable from "react-data-table-component";
import SubscriptionService from "../services/Subscription";
import { useNavigate } from 'react-router-dom';
import guLocale from "../locales/gu";
import { useAuth } from "../context/AuthContext";

const ProfileForm = () => {
  const locale = guLocale.profile;
  const localeGlobal = guLocale.global;
  const { updateUserDetails } = useAuth();
  const paginationComponentOptions = {
    rowsPerPageText: localeGlobal.rowsPerPageText, // Gujarati text for "Rows per page"
    rangeSeparatorText: localeGlobal.rangeSeparatorText, // Gujarati text for "of"
    selectAllRowsItem: true,
    selectAllRowsItemText: localeGlobal.selectAllRowsItemText, // Gujarati text for "All"
  };
  const profileForm = useForm({
    defaultValues: {
      brc_full_name: "", 
      school_name: "", 
      school_dice_code: "",
      rojmel_name: "",
      cluster_name: "",
      block_name: "",
      bank_name: "",
      bank_branch_name: "",
      bank_account_no: "",
      business_email: "",
      mobile_no: "",
      district: "",
      sub_division: "",
      pincode: "",
      address: ""
    }
  });
  const settingsForm = useForm({
    defaultValues: {
      financialYear: "",
    }
  });
  const { setIsLoading } = useLoading();
  const [financialYears, setFinancialYears] = useState([]);
  const [plansHistory, setPlansHistory] = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState("");
  const [profileId, setProfileId] = useState(null);
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();

  const columns = [
    {
       name: locale.plans.srNo,
       selector: (row, index) => index + 1,
       sortable: false,
       width: '80px',
    },
    { name: locale.plans.planName, selector: (row) => row.plan_name, sortable: true },
    { name: locale.plans.description, selector: (row) => row.description, sortable: true },
    { name: locale.plans.planId, selector: (row) => row.plan_id, sortable: true },
    { name: locale.plans.amount, selector: (row) => "₹"+ row.amount, sortable: true }
  ];

  const isFetched = useRef(false);
  const fetchAllData = useCallback(async () => {
    if (isFetched.current) return; // Prevent re-fetching
    isFetched.current = true;
  
    try {
      setIsLoading(true);
      const [profileResponse, subscriptionPlanReponse, plansHistoryResponse, financialYearsReponse] = await Promise.all([
        ProfileService.getProfile(),
        SubscriptionService.getSubscriptionPlans(),
        SubscriptionService.getPlansHistory(),
        ProfileService.getFinancialYears(),
      ]);
      if(profileResponse && profileResponse.statusCode === 200) {
        setProfileId(profileResponse?.data?._id);
        setSelectedFinancialYear(profileResponse?.data?.subscription?._id);
        settingsForm.reset({ financialYear: profileResponse?.data?.subscription?._id });
        profileForm.reset(profileResponse?.data);
      }
      if(subscriptionPlanReponse && subscriptionPlanReponse.statusCode === 200) {
        setPlans(subscriptionPlanReponse?.data);
      }
      if(plansHistoryResponse && plansHistoryResponse.statusCode === 200) {
        setPlansHistory(plansHistoryResponse?.data || []);
      }
      if(financialYearsReponse && financialYearsReponse.statusCode === 200) {
        setFinancialYears(financialYearsReponse.data);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [profileForm, settingsForm, setIsLoading]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const onSubmitProfile = async (data) => {
    setIsLoading(true);
    try {
      const payload = {
        brc_full_name: data.brc_full_name,
        school_name: data.school_name,
        school_dice_code: data.school_dice_code,
        rojmel_name: data.rojmel_name,
        cluster_name: data.cluster_name,
        block_name: data.block_name,
        bank_name: data.bank_name,
        bank_branch_name: data.bank_branch_name,
        bank_account_no: data.bank_account_no,
        business_email: data.business_email,
        mobile_no: data.mobile_no,
        district: data.district,
        sub_division: data.sub_division,
        pincode: data.pincode,
        address: data.address,
      };
      const response = await ProfileService.updateProfile(profileId, payload);
      if (response && response.statusCode === 200) {
        updateUserDetails(response.data);
        showSuccessToast(response.message);
      }
    } catch (error) {
      showErrorToast("Error updating profile.");
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitFinancialYear = async () => {
    setIsLoading(true);
    try {
      const updateFYearData = {
        current_subscription_id: selectedFinancialYear,
      };
      const response = await ProfileService.updateFinancialYear(
        profileId,
        updateFYearData
      );
      if (response && response.statusCode === 200) {
        showSuccessToast(response.message);
      }
    } catch (error) {
      showErrorToast("Error updating financial year.");
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToPricing = () => {
    navigate('/pricing');
  };

  return (
    <div>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">{locale.title}</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/">{locale.home}</a>
                  </li>
                  <li className="breadcrumb-item active">{locale.title}</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header p-2">
                    <ul className="nav nav-pills">
                      <li className="nav-item">
                        <a
                          className="nav-link active"
                          href="#profiles"
                          data-toggle="tab"
                        >
                          {locale.title}
                        </a>
                      </li>
                      <li className="nav-item">
                        <a
                          className="nav-link"
                          href="#settings"
                          data-toggle="tab"
                        >
                          {locale.settingTab}
                        </a>
                      </li>
                      <li className="nav-item">
                        <a className="nav-link" href="#plans" data-toggle="tab">
                          {locale.plansTab}
                        </a>
                      </li>
                      <li className="nav-item">
                        <a className="nav-link" href="#plansHistory" data-toggle="tab">
                          {locale.planHistory}
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="card-body">
                    <div className="tab-content">
                      <div className="tab-pane" id="settings">
                        <form
                          className="form-horizontal"
                          onSubmit={settingsForm.handleSubmit(onSubmitFinancialYear)}
                        >
                          <Row className="mb-1">
                            <Col>
                              <Form.Group className="mb-3">
                                <Form.Label>{locale.settings.financialYearLabel}</Form.Label>
                                <Form.Control
                                  as="select"
                                  id="financialYear"
                                  {...settingsForm.register("financialYear", { required: locale.settings.financialYearRequired })}
                                  onChange={(e) => {
                                    setSelectedFinancialYear(e.target.value);
                                    settingsForm.clearErrors("financialYear");
                                  }}
                                  isInvalid={!!settingsForm.formState.errors.financialYear}
                                >
                                  <option value="">
                                    {locale.settings.selectFinancialYear}
                                  </option>
                                  {financialYears.map((financialYear) => (
                                    <option
                                      key={financialYear?._id}
                                      value={financialYear?._id}
                                    >
                                      {
                                        financialYear?.financialData
                                          ?.financial_tag
                                      }
                                    </option>
                                  ))}
                                </Form.Control>
                                <Form.Control.Feedback type="invalid">
                                {settingsForm.formState.errors.financialYear?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>

                          <div className="form-group row">
                            <div className="col-md-12">
                              <Button type="submit" className="btn btn-primary">
                                {locale.settings.saveButtonText}
                              </Button>
                            </div>
                          </div>
                        </form>
                      </div>
                      <div className="active tab-pane" id="profiles">
                        <form
                          className="form-horizontal"
                          onSubmit={profileForm.handleSubmit(onSubmitProfile)}
                        >
                          <Row className="mb-3">
                            <Col>
                              <Form.Group controlId="brc_full_name" className="mb-1">
                                <Form.Label>{locale.profileForm.principle}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.principlePlaceholder}
                                  {...profileForm.register("brc_full_name", {
                                    required: locale.profileForm.principleRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.brc_full_name}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.brc_full_name?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col>
                              <Form.Group controlId="school_name" className="mb-1">
                                <Form.Label>{locale.profileForm.schoolName}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.schoolNamePlaceholder}
                                  {...profileForm.register("school_name", {
                                    required: locale.profileForm.schoolNameRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.school_name}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.school_name?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col>
                              <Form.Group controlId="school_dice_code" className="mb-1">
                                <Form.Label>{locale.profileForm.diceCode}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.diceCodePlaceholder}
                                  {...profileForm.register("school_dice_code", {
                                    required: locale.profileForm.diceCodeRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.school_dice_code}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.school_dice_code?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col>
                              <Form.Group controlId="rojmel_name" className="mb-1">
                                <Form.Label>{locale.profileForm.rojmelName}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.rojmelNamePlaceholder}
                                  {...profileForm.register("rojmel_name", {
                                    required: locale.profileForm.rojmelNameRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.rojmel_name}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.rojmel_name?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col>
                              <Form.Group controlId="cluster_name" className="mb-1">
                                <Form.Label>{locale.profileForm.clusterName}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.clusterNamePlaceholder}
                                  {...profileForm.register("cluster_name", {
                                    required: locale.profileForm.clusterNameRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.cluster_name}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.cluster_name?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col>
                              <Form.Group controlId="block_name" className="mb-1">
                                <Form.Label>{locale.profileForm.blockName}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.blockNamePlaceholder}
                                  {...profileForm.register("block_name", {
                                    required: locale.profileForm.blockNameRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.block_name}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.block_name?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col>
                              <Form.Group controlId="bank_name" className="mb-1">
                                <Form.Label>{locale.profileForm.bankName}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.bankNamePlaceholder}
                                  {...profileForm.register("bank_name", {
                                    required: locale.profileForm.bankNameRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.bank_name}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.bank_name?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col>
                              <Form.Group controlId="bank_branch_name" className="mb-1">
                                <Form.Label>{locale.profileForm.branchName}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.branchNamePlaceholder}
                                  {...profileForm.register("bank_branch_name", {
                                    required: locale.profileForm.bankNameRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.bank_branch_name}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.bank_branch_name?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col>
                              <Form.Group controlId="bank_account_no" className="mb-1">
                                <Form.Label>{locale.profileForm.bankAccountNumber}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.bankAccountNumberPlaceholder}
                                  {...profileForm.register("bank_account_no", {
                                    required: locale.profileForm.bankAccountNumberRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.bank_account_no}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.bank_account_no?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col>
                              <Form.Group controlId="business_email" className="mb-1">
                                <Form.Label>{locale.profileForm.email}</Form.Label>
                                <Form.Control
                                  type="email"
                                  placeholder={locale.profileForm.emailPlaceholder}
                                  {...profileForm.register("business_email", {
                                    required: locale.profileForm.emailRequired,
                                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: locale.profileForm.emailInvalid },
                                  })}
                                  isInvalid={!!profileForm.formState.errors.business_email}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.business_email?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                          <Col>
                            <Form.Group controlId="mobile_no" className="mb-1">
                              <Form.Label>{locale.profileForm.mobile}</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder={locale.profileForm.mobilePlaceholder}
                                {...profileForm.register("mobile_no", {
                                  required: locale.profileForm.mobileRequired,
                                  pattern: { value: /^[0-9]{10}$/, message: locale.profileForm.mobileInvalid },
                                })}
                                isInvalid={!!profileForm.formState.errors.mobile_no}
                              />
                              <Form.Control.Feedback type="invalid">
                                {profileForm.formState.errors.mobile_no?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                            <Col>
                              <Form.Group controlId="district" className="mb-1">
                                <Form.Label>{locale.profileForm.district}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.districtPlaceholder}
                                  {...profileForm.register("district", {
                                    required: locale.profileForm.districtRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.district}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.district?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col>
                              <Form.Group controlId="sub_division" className="mb-1">
                                <Form.Label>{locale.profileForm.subDivision}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.subDivisionPlaceholder}
                                  {...profileForm.register("sub_division", {
                                    required: locale.profileForm.subDivisionRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.sub_division}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.sub_division?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col>
                              <Form.Group controlId="pincode" className="mb-1">
                                <Form.Label>{locale.profileForm.pincode}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.pincodePlaceholder}
                                  {...profileForm.register("pincode", {
                                    required: locale.profileForm.pincodeRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.pincode}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.pincode?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col>
                              <Form.Group controlId="address" className="mb-1">
                                <Form.Label>{locale.profileForm.address}</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={locale.profileForm.addressPlaceholder}
                                  {...profileForm.register("address", {
                                    required: locale.profileForm.addressRequired,
                                  })}
                                  isInvalid={!!profileForm.formState.errors.address}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileForm.formState.errors.address?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>

                          <div className="form-group row">
                            <div className="col-md-12">
                              <Button type="submit" className="btn btn-primary">
                                {locale.profileForm.updateProfile}
                              </Button>
                            </div>
                          </div>
                        </form>
                      </div>
                      <div className="tab-pane" id="plans">
                        <Container fluid>
                          <Row>
                            {plans.map((plan) => (
                                <Col md={4} className="mb-4 mx-3 d-flex" key={plan._id}>
                                  <Card className="shadow-lg border-0 flex-fill" style={{ backgroundColor: "#ffffff", borderRadius: '10px', display: 'flex', flexDirection: 'column' }}>
                                      <Card.Body style={{ padding: '25px', textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
                                          <div style={{marginBottom: '20px'}}>
                                              <h6 className="text-muted mb-2" style={{ fontWeight: 'bold', color: '#777', fontSize: '0.9rem' }}>નાણાંકીય વર્ષ</h6>
                                              <h5 className="fw-bold" style={{ fontSize: '1.3rem', marginBottom: '10px', color: '#555' }}>{plan.plan_name}</h5>
                                              <h3 className="text-primary" style={{ fontSize: '2.2rem', marginBottom: '15px' }}>₹{plan.amount}</h3>
                                              <p style={{ color: '#777', marginBottom: '20px', fontSize: '1rem' }}>{plan.description}</p>
                                          </div>
                                          <ul className="list-unstyled flex-grow-1">
                                              <li><span style={{ color: 'green', marginRight: '5px' }}>✓</span> કેશબુક રિપોર્ટ</li>
                                              <li><span style={{ color: 'green', marginRight: '5px' }}>✓</span> ખાતાવહી</li>
                                              <li><span style={{ color: 'green', marginRight: '5px' }}>✓</span> પરિશિષ્ટ 9</li>
                                              <li><span style={{ color: 'green', marginRight: '5px' }}>✓</span> પરિશિષ્ટ 10</li>
                                              <li><span style={{ color: 'green', marginRight: '5px' }}>✓</span> બિલ રજિસ્ટર</li>
                                              <li><span style={{ color: 'green', marginRight: '5px' }}>✓</span> ચેક રજિસ્ટર</li>
                                              <li><span style={{ color: 'green', marginRight: '5px' }}>✓</span> ગ્રાન્ટ રજિસ્ટર</li>
                                          </ul>
                                          <Button variant="primary" className="w-100" onClick={() => handleGoToPricing()} disabled={plan.isPurchased}>{ plan.isPurchased ? locale.plans.alreadySubscribed : locale.plans.subscribe }</Button>
                                      </Card.Body>
                                  </Card>
                              </Col>
                            ))}
                          </Row>
                        </Container>
                      </div>
                      <div className="tab-pane" id="plansHistory">
                        <Container fluid>
                          <Row>
                            <Col>
                              <DataTable
                                columns={columns}
                                data={plansHistory}
                                pagination
                                paginationComponentOptions={paginationComponentOptions}
                              />
                            </Col>
                          </Row>
                        </Container>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProfileForm;
